import { lazy, Dispatch, useEffect, useState, ComponentType } from 'react'
import { Icomponent } from '../interfaces'
import {
  IComponentWrapperProps,
  IComponentPromise,
  IComponentPromises,
} from '../interfaces/templates'
import ScrollHider from '../components/ScrollHider'

const importView = <T,>(route: string, isExternal: boolean) =>
  isExternal
    ? lazy<ComponentType<T>>(() => import(/* webpackIgnore: true */ route))
    : lazy<ComponentType<T>>(() =>
        import('@engyalo/delivery-ui-components').then(
          (module: Record<string, any>) => ({ default: module[route] }),
        ),
      )

const getLoadViewsPromises = async (
  componentsList: Array<Icomponent>,
): Promise<Promise<IComponentPromise>[]> => {
  return componentsList.map(async ({ uid, importRoute, isExternal }) => {
    const View = await importView<{ id: string }>(importRoute, !!isExternal)
    const Wrapper = ({
      containerSection,
      parentUid,
    }: IComponentWrapperProps) => {
      const cinnamonProps =
        containerSection.components?.filter(
          ({ uid: componentUid }) => componentUid === uid,
        )[0]?.props || {}

      const [runnedOnloadAction, setRunnedOnLoadAction] =
        useState<boolean>(false)
      const { runOnLoad, hideOnScroll, hideOnScrollOffset, ...rest } =
        cinnamonProps

      useEffect(() => {
        if (!runnedOnloadAction && runOnLoad) {
          runOnLoad()
          setRunnedOnLoadAction(true)
        }
      }, [runOnLoad, runnedOnloadAction])
      const finalProps = {
        ...rest,
        id: `${uid}-${parentUid}`,
      }

      return hideOnScroll ? (
        <ScrollHider offset={hideOnScrollOffset}>
          <View {...finalProps} />
        </ScrollHider>
      ) : (
        <View {...finalProps} />
      )
    }
    return { wrapper: Wrapper, uid }
  })
}

const loadViews = async (
  componentsList: Array<Icomponent>,
  stateSetter: Dispatch<React.SetStateAction<IComponentPromises>>,
): Promise<void> => {
  const componentPromises: Promise<IComponentPromise>[] =
    await getLoadViewsPromises(componentsList)

  Promise.all(componentPromises).then(stateSetter)
}

export { importView, getLoadViewsPromises }
export default loadViews
